'use-client';
import type { FC } from 'react';
import React, { useState, useEffect } from 'react';

import classNames from 'clsx';
import { getDataAttributes } from '@wix/editor-elements-common-utils';

import type { ILoginSocialBarProps } from '../LoginSocialBar.types';
import LoggedInSocialBar from './components/loggedInSocialBar';
import LoggedOffSocialBar from './components/loggedOffSocialBar';
import {
  BIAction,
  getMembersAreaNavigationEvent,
  MEMBERS_APP_UOU_ENDPOINT,
} from './bi/events';

import styles from './style/LoginSocialBar.scss';

const LoginSocialBar: FC<ILoginSocialBarProps> = props => {
  const {
    id,
    className,
    isLoggedIn,
    userName,
    loggedInTextState,
    showLoggedInText,
    loggedInText,
    customLoggedInText,
    direction,
    useNativeMenu,
    menuItems,
    iconItems,
    currentPrimaryPageHref,
    reportBiOnClick,
    onLogin,
    onLogout,
    navigateTo,
    onMouseEnter,
    onMouseLeave,
    isMenuOpen,
    onMenuOpen,
    onMenuClose,
    reportBiOnMenuItemClick,
    translations,
    avatarUri,
    iconSize,
    showBellIcon,
    arrowShape,
    menuItemsAlignment,
    reportBi,
    displayMode,
    loggedInAvatarIcon,
    loggedOutDisplayMode,
    loggedOutAvatarIcon,
    loggedInCustomAvatar,
    loggedOutCustomAvatar,
    loggedOutIconSize,
    enableLoggingInStateInSSR,
  } = props;

  const [isHydrated, setIsHydrated] = useState(false);

  // only show logged in once hydration finishes for caching
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const closeMenuOnClickOutside: React.FocusEventHandler = ({
    currentTarget,
    relatedTarget,
  }) => {
    if (!relatedTarget || !currentTarget.contains(relatedTarget as Node)) {
      onMenuClose();
    }
  };

  const showIcons = showBellIcon && !!iconItems.length;

  const onHover = (event: React.MouseEvent<Element, MouseEvent>) => {
    onMouseEnter?.(event);
    if (isLoggedIn && !isMenuOpen) {
      void reportBi(getMembersAreaNavigationEvent({ action: BIAction.Hover }), {
        endpoint: MEMBERS_APP_UOU_ENDPOINT,
      });
    }
  };

  const openMenu = () => {
    onMenuOpen();
    if (isLoggedIn) {
      void reportBi(
        getMembersAreaNavigationEvent({ action: BIAction.MenuClick }),
        {
          endpoint: MEMBERS_APP_UOU_ENDPOINT,
        },
      );
    }
  };

  const shouldShowLoggedIn = enableLoggingInStateInSSR
    ? isHydrated && isLoggedIn
    : isLoggedIn;

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classNames(className, styles.root)}
      tabIndex={-1}
      onBlur={
        isLoggedIn && !useNativeMenu ? closeMenuOnClickOutside : undefined
      }
      onMouseEnter={onHover}
      onMouseLeave={onMouseLeave}
    >
      {shouldShowLoggedIn ? (
        <LoggedInSocialBar
          avatarUri={avatarUri}
          isLoggedIn={isLoggedIn}
          userName={userName}
          id={id}
          displayMode={displayMode}
          iconSize={iconSize}
          loggedInTextState={loggedInTextState}
          showLoggedInText={showLoggedInText}
          loggedInText={loggedInText}
          customLoggedInText={customLoggedInText}
          currentPrimaryPageHref={currentPrimaryPageHref}
          direction={direction}
          menuItems={menuItems}
          iconItems={iconItems}
          showIcons={showIcons}
          isMenuOpen={isMenuOpen}
          onMenuOpen={openMenu}
          reportBiOnMenuItemClick={reportBiOnMenuItemClick}
          navigateTo={navigateTo}
          onLogout={onLogout}
          onMenuClose={onMenuClose}
          avatarIcon={loggedInAvatarIcon}
          customAvatar={loggedInCustomAvatar}
          useNativeMenu={useNativeMenu}
          translations={translations}
          arrowShape={arrowShape}
          menuItemsAlignment={menuItemsAlignment}
          reportBi={reportBi}
          reportBiOnClick={reportBiOnClick}
        />
      ) : (
        <LoggedOffSocialBar
          id={id}
          displayMode={loggedOutDisplayMode}
          iconSize={loggedOutIconSize}
          avatarIcon={loggedOutAvatarIcon}
          customAvatar={loggedOutCustomAvatar}
          handleLogin={onLogin}
          reportBiOnClick={reportBiOnClick}
          translations={translations}
        />
      )}
    </div>
  );
};

export default LoginSocialBar;
